import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateActivityLimitDto,
  ActivityLimitDto,
  Side,
  IsotopeDto,
} from '@services/dtos'
import { activityLimitSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Grid, Typography } from '@mui/material'
import {
  createActivityLimit,
  updateActivityLimit,
} from '@state/reducers/activityLimitReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormCrudHeader } from './FormCrudHeader'
import InputField from '../inputs/InputField'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import IsotopeForm from './IsotopeForm'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { isotopeService } from '@services/IsotopeService'
import SelectField from '../inputs/SelectField'
import { Site } from '@/services/api'

type Props = {
  editingActivityLimit?: ActivityLimitDto | null
  onClose: () => void
}

const ActivityLimitForm: React.FC<Props> = ({
  editingActivityLimit,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm<CreateActivityLimitDto>({
    defaultValues: editingActivityLimit
      ? {
          ...editingActivityLimit,
          siteId: editingActivityLimit.site.id,
          isotopeId: editingActivityLimit.isotope.id,
        }
      : {},
    resolver: yupResolver(activityLimitSchema),
  })

  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateActivityLimitDto) => {
    setIsSubmitting(true)

    if (editingActivityLimit) {
      dispatch(updateActivityLimit({ id: editingActivityLimit.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('La limite d\'activité a été modifiée avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le combo site/isotope n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createActivityLimit(data))
        .unwrap()
        .then(() => {
          cb('La limite d\'activité a été créée avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le combo site/isotope n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields = [
    {
      name: 'maxActivityMbq',
      label: 'Activité max (MBq)',
      sm: 6,
      type: 'number',
    },
    {
      name: 'authorizationNumber',
      label: 'N° autorisation',
      sm: 6,
      type: 'text',
    },
    { name: 'notes', label: 'Notes', sm: 12, type: 'textarea' },
  ]
  const renderFields = (
    fields: { name: string; label: string; type?: string; sm?: number }[],
  ) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
          helperText={
            errors[field.name as keyof CreateActivityLimitDto]?.message
          }
          error={!!errors[field.name as keyof CreateActivityLimitDto]}
        />
      </Grid>
    ))
  }

  const fetchIsotopes = async (search: string) => {
    const page = await isotopeService.getAll({ search })
    return page?.datas ?? []
  }

  const sites = useMemo(() => (user as any & { sites: Site[] })?.sites, [user])

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingActivityLimit}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Limite activité
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Isotope"
                  addTooltipTitle="Ajouter un isotope"
                  dialogContent={(close) => (
                    <IsotopeForm onClose={close} />
                  )}
                  input={
                    <ServerAutocomplete<IsotopeDto>
                      fetchService={fetchIsotopes}
                      onChange={(value: IsotopeDto | null) => {
                        setValue('isotopeId', value?.id ?? '')
                      }}
                      getValue={(value) => value?.id?.toString() || null}
                      getOptionLabel={(value) => {
                        const isotope = value as IsotopeDto
                        return isotope
                          ? `${isotope.name} (${isotope.code})`
                          : ''
                      }}
                      debounceMs={300}
                      placeholder="Rechercher un isotope"
                      label="Isotope"
                      helperText={errors.isotopeId?.message}
                      error={!!errors.isotopeId}
                      minSearchLength={0}
                      initialOptions={
                        editingActivityLimit?.isotope
                          ? [editingActivityLimit.isotope]
                          : []
                      }
                      value={editingActivityLimit?.isotope ?? null}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {sites?.length ? (
                  <SelectField
                    control={control}
                    name="siteId"
                    options={sites.map(({ id, label }) => ({
                      label,
                      value: id,
                    }))}
                    value={watch('siteId')}
                    label="Site"
                  />
                ) : (
                  <Typography variant="body1">Aucun site enregistré</Typography>
                )}
              </Grid>

              {renderFields(fields)}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default ActivityLimitForm
